package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.core.network.ServerDated
import fr.frux.core.network.serverTime
import fr.frux.shared.dto.AccountExportDto
import fr.frux.shared.dto.AuthResponse
import fr.frux.shared.dto.CurrentUserDto
import fr.frux.shared.dto.DeleteAccountRequest
import fr.frux.shared.dto.LogoutRequest
import fr.frux.shared.dto.RefreshRequest
import fr.frux.shared.dto.RequestCodeRequest
import fr.frux.shared.dto.RequestCodeResponse
import fr.frux.shared.dto.VerifyCodeRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody

/**
 * API interface for authentication operations (ADR 0012, passwordless OTP email).
 */
interface AuthApi {
    /**
     * Demande l'envoi d'un code à [email]. Répond 200 que l'adresse ait ou non un compte
     * (ADR 0012) : [RequestCodeResponse.devCode] n'est renseigné qu'en environnement de dev/test
     * et le client l'ignore.
     *
     * [purpose] borne ce que le code prouvera à la vérification (#274) : `null` (défaut) demande
     * un code de connexion, `"DELETE_ACCOUNT"` un code de confirmation de suppression. Un code
     * de connexion ne peut pas confirmer une suppression, et inversement.
     */
    suspend fun requestCode(
        email: String,
        purpose: String? = null,
    ): RequestCodeResponse

    /**
     * Vérifie le code reçu. [username] n'est fourni que lorsque le serveur a préalablement
     * répondu `USERNAME_REQUIRED` : c'est la même requête, re-présentée avec le pseudo choisi.
     */
    suspend fun verifyCode(
        email: String,
        code: String,
        username: String? = null,
    ): AuthResponse

    /**
     * Compte courant et ses droits Frux+ (JWT requis, #454). Les droits sont relus en base par le
     * serveur à chaque appel, jamais portés par le jeton. L'heure du serveur accompagne la réponse :
     * les échéances des droits se décomptent à partir d'elle, pas de l'horloge de l'appareil (#527).
     */
    suspend fun getCurrentUser(): ServerDated<CurrentUserDto>

    /**
     * Fait tourner [token] (le secret opaque `frxr1_...`, jamais l'access JWT) contre une paire
     * neuve (#26, ADR 0019). Sans JWT côté transport : un access expiré ne doit pas empêcher de
     * rafraîchir.
     */
    suspend fun refreshToken(token: String): AuthResponse

    /**
     * Révoque la famille de sessions portée par [refreshToken] — c'est l'appareil courant (#26,
     * ADR 0019). Toujours silencieux côté serveur (204 systématique, y compris token inconnu) :
     * l'appelant purge ses credentials locaux quoi qu'il arrive.
     */
    suspend fun logout(refreshToken: String)

    /**
     * « Déconnecter tous les appareils » (#26, ADR 0019) : révoque toutes les familles de
     * l'utilisateur courant, JWT requis.
     */
    suspend fun logoutAll()

    /**
     * Efface définitivement le compte courant (#172). Le code OTP reconfirme le geste : un
     * jeton volé ne doit pas suffire à détruire un compte (ADR 0012).
     */
    suspend fun deleteAccount(code: String)

    /**
     * Export de portabilité du compte courant (#172).
     */
    suspend fun exportAccount(): AccountExportDto
}

/**
 * Implementation of AuthApi using Ktor HttpClient.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class AuthApiImpl(
    private val httpClient: HttpClient,
) : AuthApi {
    override suspend fun requestCode(
        email: String,
        purpose: String?,
    ): RequestCodeResponse =
        httpClient
            .post("${ApiPaths.V1}/auth/request-code") {
                setBody(RequestCodeRequest(email, purpose))
            }.body()

    override suspend fun verifyCode(
        email: String,
        code: String,
        username: String?,
    ): AuthResponse =
        httpClient
            .post("${ApiPaths.V1}/auth/verify-code") {
                setBody(VerifyCodeRequest(email = email, code = code, username = username))
            }.body()

    override suspend fun getCurrentUser(): ServerDated<CurrentUserDto> {
        val response = httpClient.get("${ApiPaths.V1}/auth/me")
        return ServerDated(body = response.body(), serverTime = response.serverTime())
    }

    override suspend fun refreshToken(token: String): AuthResponse =
        httpClient
            .post("${ApiPaths.V1}/auth/refresh") {
                setBody(RefreshRequest(token))
            }.body()

    override suspend fun logout(refreshToken: String) {
        httpClient.post("${ApiPaths.V1}/auth/logout") {
            setBody(LogoutRequest(refreshToken))
        }
    }

    override suspend fun logoutAll() {
        httpClient.post("${ApiPaths.V1}/auth/logout-all")
    }

    override suspend fun deleteAccount(code: String) {
        // Le code OTP de reconfirmation faux rend 400 `INVALID_CODE`, pas 401 (#26, ADR 0019) :
        // avec l'auto-refresh client, un 401 métier deviendrait ambigu avec une session
        // réellement morte.
        httpClient.delete("${ApiPaths.V1}/auth/me") {
            setBody(DeleteAccountRequest(code))
        }
    }

    override suspend fun exportAccount(): AccountExportDto = httpClient.get("${ApiPaths.V1}/auth/me/export").body()
}
